// AddClient.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Classes from "./AddClient.module.css";
import Step1ClientDetails from "./Comp/Step1ClientDetails";
import Step2VehicleInfo from "./Comp/Step2VehicleInfo";
import { CarFrontIcon, ProfileIcon } from "../../../../../../utils/Icons";
import { uploadMedia } from "../../../../../../utils/uploadMedia";
import backend from "../../../../../../api";

const EMPTY_CLIENT = {
  client: {
    firstName: "", lastName: "", email: "", password: "",
    phone: "",
    address1: "", city: "", country: "", region: "", postalCode: "",
    neighborhood: "", company: "", gateCode: "", parkingSpot: "",
    documents: [], // File[]
  },
  vehicle: { make: "", model: "", license: "", additionalVehicles: [] },
};

const STORAGE_KEY = "clientCreateDraft";
const slideVariants = { initial: { x: 10, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: -10, opacity: 0 } };

export default function AddClient() {
  const { id } = useParams();                      // 👈 edit if present
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const onBoarding = useSelector((s) => s.companyInfo?.info);
  const companyId = onBoarding?._id;

  const [activeStep, setActiveStep] = useState(1);
  const [draft, setDraft] = useState(EMPTY_CLIENT);
  const [loading, setLoading] = useState(false);

  // load from storage (only for "add")
  useEffect(() => {
    if (isEdit) return;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed.activeStep) setActiveStep(parsed.activeStep);
      if (parsed.draft) setDraft(parsed.draft);
    } catch {}
  }, [isEdit]);

  // persist (avoid storing File objects)
  useEffect(() => {
    if (isEdit) return; // don't persist while editing
    const replacer = (_k, v) => (v instanceof File || v?.startsWith?.("blob:") ? undefined : v);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ activeStep, draft }, replacer));
  }, [activeStep, draft, isEdit]);

  const go = (n) => setActiveStep(n);
  const next = () => setActiveStep((p) => Math.min(p + 1, 2));
  const back = () => setActiveStep((p) => Math.max(p - 1, 1));

  const setClient = useCallback((partial) => {
    setDraft((prev) => ({ ...prev, client: { ...prev.client, ...partial } }));
  }, []);
  const setVehicle = useCallback((partial) => {
    setDraft((prev) => ({ ...prev, vehicle: { ...prev.vehicle, ...partial } }));
  }, []);

  const addDocuments = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setDraft((p) => ({ ...p, client: { ...p.client, documents: [...p.client.documents, ...arr] } }));
  };
  const removeDocument = (idx) => {
    setDraft((p) => {
      const copy = [...p.client.documents];
      copy.splice(idx, 1);
      return { ...p, client: { ...p.client, documents: copy } };
    });
  };

  // Map user → draft for edit
  const userToDraft = (u) => ({
    client: {
      firstName: u.firstName || "", lastName: u.lastName || "",
      email: u.email || "", password: "", // do not prefill
      phone: u.phone || "",
      address1: u.customerProfile?.location?.address || "",
      city: u.customerProfile?.location?.city || "",
      region: u.customerProfile?.location?.state || "",
      postalCode: u.customerProfile?.location?.postalCode || "",
      country: u.customerProfile?.location?.country || "",
      neighborhood: u.customerProfile?.location?.neighborhood || "",
      company: u.customerProfile?.location?.company || "",
      gateCode: u.customerProfile?.location?.gateCode || "",
      parkingSpot: u.customerProfile?.location?.parkingSpot || "",
      documents: [], // new files only; existing docs managed in Client → Documents tab
    },
    vehicle: {
      make: u.customerProfile?.vehicle?.vehicles?.[0]?.make || "",
      model: u.customerProfile?.vehicle?.vehicles?.[0]?.model || "",
      license: u.customerProfile?.vehicle?.vehicles?.[0]?.plate || "",
      additionalVehicles: (u.customerProfile?.vehicle?.vehicles || []).slice(1).map(v => ({
        make: v.make || "", model: v.model || "", license: v.plate || "",
      })),
    },
  });

  // Load for edit
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    setLoading(true);
    backend.get(`/api/user/${id}`)
      .then(({ data }) => {
        if (!alive) return;
        const u = data?.data;
        if (u) setDraft(userToDraft(u));
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, isEdit]);

  const clearDraftAndExit = () => {
    if (!isEdit) localStorage.removeItem(STORAGE_KEY);
    setDraft(EMPTY_CLIENT);
    setActiveStep(1);
    navigate("/clients");
  };

  const handleFinish = async () => {
    if (!companyId) return alert("Missing companyId.");
    const c = draft.client;
    const v = draft.vehicle;

    try {
      setLoading(true);

      // 1) upload new docs (if any)
      const uploaded = [];
      for (const f of c.documents || []) {
        const url = await uploadMedia([f]); // returns first url
        uploaded.push({ url, title: f.name, kind: f.type, size: f.size });
      }

      // 2) build vehicles array (schema uses plate)
      const vehicles = [
        { make: v.make || "", model: v.model || "", plate: v.license || "" },
        ...(v.additionalVehicles || []).map(av => ({
          make: av.make || "", model: av.model || "", plate: av.license || "",
        })),
      ].filter(x => x.make || x.model || x.plate); // drop empty

      // 3) build location
      const location = {
        address: c.address1, city: c.city, state: c.region,
        postalCode: c.postalCode, country: c.country,
        neighborhood: c.neighborhood, company: c.company,
        gateCode: c.gateCode, parkingSpot: c.parkingSpot,
      };

      if (isEdit) {
        // EDIT: merge main fields
        await backend.put(`/api/user/${id}`, {
          email: c.email,
          firstName: c.firstName, lastName: c.lastName, phone: c.phone,
          name: [c.firstName, c.lastName].filter(Boolean).join(" "),
          customerProfile: { location, vehicle: { vehicles } },
        });

        // add any NEW docs to user.documents
        for (const d of uploaded) {
          await backend.post(`/api/user/${id}/documents`, d);
        }
      } else {
        // CREATE
        await backend.post(`/api/user/admin/add-user`, {
          email: c.email,
          password: c.password,              // required for create
          firstName: c.firstName, lastName: c.lastName, phone: c.phone,
          companyId,
          location,
          vehicles,
          documents: uploaded,
        });
      }

      clearDraftAndExit();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || e.message || "Failed to save client");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, label: "Client Details", icon: <ProfileIcon height="18px" fill="white" /> },
    { id: 2, label: "Vehicle Info",  icon: <CarFrontIcon height="18px" fill="white" /> },
  ];

  return (
    <div className={Classes.hero}>
      <div className={Classes.body}>
        <div className={Classes.bodyInner}>
          {/* Stepper */}
          <motion.div className={Classes.stepper} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ type: "tween", duration: 0.35, delay: 0.1 }}>
            {steps.map((s, i) => (
              <React.Fragment key={s.id}>
                <div role="button" onClick={() => go(s.id)} className={`${Classes.sideElement} ${activeStep === s.id ? Classes.current : ""}`}>
                  <div className={Classes.flexAlign10}>
                    <div className={`${Classes.imageCon} ${activeStep >= s.id ? Classes.active : ""}`}>{s.icon}</div>
                    <h5 className={activeStep >= s.id ? Classes.activeText : undefined}>{s.label}</h5>
                  </div>
                </div>
                {i !== steps.length - 1 && <div className={`${Classes.hr} ${activeStep > s.id ? Classes.coloredHr : ""}`} />}
              </React.Fragment>
            ))}
          </motion.div>

          {/* Animated content */}
          <AnimatePresence mode="wait">
            <motion.div key={activeStep} variants={slideVariants} initial="initial" animate="animate" exit="exit" className={Classes.infoMain}>
              {activeStep === 1 && (
                <Step1ClientDetails
                  draft={draft}
                  setClient={setClient}
                  addDocuments={addDocuments}
                  removeDocument={removeDocument}
                  next={next}
                  isEdit={isEdit}
                />
              )}

              {activeStep === 2 && (
                <Step2VehicleInfo
                  draft={draft}
                  setVehicle={setVehicle}
                  back={back}
                  finish={handleFinish}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {loading && <div style={{ position:"fixed", inset:0, background:"rgba(255,255,255,.4)" }} />}
    </div>
  );
}
